/**
 * Test suite for the FFJ webhook validation & acceptance pipeline
 * (base_claim API Test tool). Every case builds a full payload from the
 * "perfect client" baseline and mutates exactly the thing under test, so the
 * UI can show the precise payload submitted and the item expected to fail.
 *
 * Cases are evaluated by the side-effect-free dry-run endpoint
 * (/api/ffj-webhook-dry-run) — nothing is ever written.
 */

export interface ExpectedResult {
    outcome: 'accepted' | 'rejected'
    status: number
    reasonCode: string | null
    /** Substring that must appear somewhere in the response body (case-insensitive). */
    detailMatch?: string
}

export interface ApiTestCase {
    id: string
    category: string
    name: string
    /** What is wrong with this payload / what the API should do with it. */
    description: string
    /** The exact field or rule under test. */
    failingItem: string
    expected: ExpectedResult
    build: (base: any) => any
}

// 1x1 transparent PNG — a genuinely valid base64 PNG for the baseline.
export const VALID_PNG_BASE64 =
    'iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mP8z8BQDwAEhQGAhKmMIQAAAABJRU5ErkJggg=='

export const UNAPPROVED_LENDER = 'Made Up Loans Ltd'

const isoDaysAgo = (days: number): string => {
    const d = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
    return d.toISOString().slice(0, 19).replace('T', ' ')
}

const isoDaysAhead = (days: number): string => {
    const d = new Date(Date.now() + days * 24 * 60 * 60 * 1000)
    return d.toISOString().slice(0, 10)
}

/**
 * The "perfect client": passes every schema rule, the lender gate (given an
 * approved lender name), all eleven eligibility checks, both aggregate rules
 * (quantum ≈ £2,100 > £1,700), the three-document requirement (CFA/LOA/AOF),
 * the signature gate and — because it carries credit_report_json — the v2
 * AML gate (identity passed & matching, 0 CCJs, no IVA, no bankruptcy).
 */
export const buildPerfectPayload = (approvedLender: string) => ({
    consumer: {
        client_id: 'TEST-CLIENT-000001',
        title: 'Mrs',
        first_name: 'Amelia',
        middle_name: null,
        last_name: 'Hartley',
        email: 'amelia.hartley@example.co.uk',
        phone: '+44 7911 123456',
        date_of_birth: '1986-03-22',
    },
    submission: {
        reference: 'TEST-SUB-000001',
        submitted_at: isoDaysAgo(1),
        status: 'completed',
    },
    enquiries: [
        {
            enquiry: { reference: 'TEST-ENQ-000001', status: 'delivered' },
            address: {
                current: {
                    line_1: '12 High Street',
                    line_2: null,
                    city: 'Manchester',
                    region: 'Greater Manchester',
                    postcode: 'M1 1AE',
                    time_at_address: '3 years',
                },
                history: [],
            },
            consent: {
                agreement_accepted: true,
                credit_check_consent: true,
                signature_captured: true,
            },
            agreements: [
                {
                    lender_name: approvedLender,
                    account_id: 'TEST-AGR-0001',
                    account_type: 'Hire Purchase',
                    status: 'closed',
                    start_date: '2018-06-01',
                    end_date: '2022-06-01',
                    original_amount: 12000,
                    balance: 0,
                    monthly_payment_amount: 250,
                    loan_term: 48,
                    payments_made: 48,
                    documents: [
                        {
                            type: 'tcs',
                            filename: 'conditional-fee-agreement-signed.pdf',
                            url: 'https://files.example.com/dl/cfa',
                            mime_type: 'application/pdf',
                        },
                        {
                            type: 'tcs',
                            filename: 'letter-of-authority-signed.pdf',
                            url: 'https://files.example.com/dl/loa',
                            mime_type: 'application/pdf',
                        },
                        {
                            type: 'tcs',
                            filename: 'authority-to-act-signed.pdf',
                            url: 'https://files.example.com/dl/aof',
                            mime_type: 'application/pdf',
                        },
                    ],
                },
            ],
        },
    ],
    documents: [
        {
            type: 'credit_search',
            filename: 'credit-report.pdf',
            reference: 'TEST-EXP-0001',
            url: 'https://files.example.com/dl/credit-report',
            mime_type: 'application/pdf',
        },
    ],
    credit_search: {
        verified_user: true,
        verification_decision: 'verified',
        search_reference: 'TEST-EXP-0001',
        bureaus_searched: ['experian'],
    },
    signature: {
        image_base64: VALID_PNG_BASE64,
        audit: {
            signed_at: isoDaysAgo(1),
            ip_address: '86.140.55.12',
            user_agent: 'Mozilla/5.0 (iPhone; CPU iPhone OS 17_0 like Mac OS X)',
            device_type: 'mobile',
            timezone: 'Europe/London',
        },
    },
    // v2 client-level artefacts — optional in the contract, included in the
    // baseline so the AML gate is exercised (and passes) on every run.
    credit_report_pdf: {
        filename: 'credit-report.pdf',
        url: 'https://files.example.com/dl/credit-report',
        mime_type: 'application/pdf',
        reference: 'TEST-EXP-0001',
    },
    aml_pdf: {
        filename: 'aml-check.pdf',
        url: 'https://files.example.com/dl/aml-check',
        mime_type: 'application/pdf',
    },
    // Real FFJ credit-report JSON shape (see the example payload they
    // provided): AML screening lives in aml_data — identityResult, the single
    // matched-name string, a ccjs[] array of judgment records and an
    // insolvencies[] array of order records.
    credit_report_json: {
        timestamp: isoDaysAgo(1),
        affiliatech_reference: 'TEST-AFF-000001',
        verified_user: 1,
        search_name: 'Amelia Hartley',
        name_variations_found: false,
        total_claims: 1,
        has_previous_industry_search: false,
        previous_industry_matches: [],
        bureaus_searched: [{ bureau_id: 'TEST-BUREAU-0001', bureau_name: 'TransUnion' }],
        claims: [
            {
                lender_name: approvedLender,
                account_id: 'TEST-AGR-0001',
                account_type: 'Hire Purchase',
                original_amount: 12000,
                start_date: '2018-06-01',
                end_date: '2022-06-01',
                status: 'closed',
                balance: 0,
                monthly_payment_amount: 250,
                payments_made: 48,
                loan_term: 48,
                credit_bureaus: [{ bureau_id: 'TEST-BUREAU-0001', bureau_name: 'TransUnion' }],
            },
        ],
        aml_data: {
            identityScore: 40,
            identityResult: 'Pass',
            pepsCount: 0,
            sanctionsCount: 0,
            deceasedGro: 'false',
            deceasedHalo: 'false',
            addressVerified: 'true',
            electoralRoll: 1,
            addressMatched: '12, HIGH STREET, MANCHESTER, M1 1AE',
            nameMatched: 'MRS AMELIA HARTLEY',
            cifas: '',
            ccjs: [],
            insolvencies: [],
        },
    },
})

// A CCJ record in the real aml_data.ccjs shape.
export const buildTestCcjRecord = (n: number) => ({
    name: 'MRS AMELIA HARTLEY',
    address: '12 HIGH STREET, MANCHESTER, M1 1AE',
    status: 'Active',
    judgmentType: 'County Court Judgment',
    judgmentDate: `2024-0${(n % 9) + 1}-08`,
    courtName: 'CIVIL NATIONAL BUSINESS CENTRE',
    caseNumber: `TESTCCJ${n}`,
    usedInDecision: 1,
})

// Insolvency order records in the real aml_data.insolvencies shape.
export const TEST_IVA_RECORD = {
    name: 'MRS AMELIA HARTLEY',
    address: '12 HIGH STREET, MANCHESTER, GBR M1 1AE',
    orderType: 'Individual Voluntary Arrangement Made',
    courtName: 'VOLUNTARY LIQUIDATION CASES',
    usedInDecision: 1,
}
export const TEST_BANKRUPTCY_RECORD = {
    name: 'MRS AMELIA HARTLEY',
    address: '12 HIGH STREET, MANCHESTER, GBR M1 1AE',
    orderType: 'Bankruptcy Order',
    courtName: 'HIGH COURT OF JUSTICE',
    usedInDecision: 1,
}

const clone = (payload: any) => JSON.parse(JSON.stringify(payload))
const agr = (p: any) => p.enquiries[0].agreements[0]
const addAgreementWithReportClaim = (p: any, overrides: Record<string, any>) => {
    const added = { ...agr(p), ...overrides }
    p.enquiries[0].agreements.push(added)
    p.credit_report_json.claims.push({
        lender_name: added.lender_name,
        account_id: added.account_id,
        account_type: added.account_type,
        original_amount: added.original_amount,
        start_date: added.start_date,
        end_date: added.end_date,
        status: added.status,
        balance: added.balance,
        monthly_payment_amount: added.monthly_payment_amount,
        payments_made: added.payments_made,
        loan_term: added.loan_term,
        credit_bureaus: added.credit_bureaus ?? [],
    })
}
const audit = (p: any) => p.signature.audit
const address = (p: any) => p.enquiries[0].address.current
const claimDocs = (): any[] => [
    {
        type: 'tcs',
        filename: 'conditional-fee-agreement-signed.pdf',
        url: 'https://files.example.com/dl/cfa',
        mime_type: 'application/pdf',
    },
    {
        type: 'tcs',
        filename: 'letter-of-authority-signed.pdf',
        url: 'https://files.example.com/dl/loa',
        mime_type: 'application/pdf',
    },
    {
        type: 'tcs',
        filename: 'authority-to-act-signed.pdf',
        url: 'https://files.example.com/dl/aof',
        mime_type: 'application/pdf',
    },
]
const removeDoc = (p: any, match: string) => {
    const docs = agr(p).documents
    agr(p).documents = docs.filter((d: any) => !String(d.filename ?? '').includes(match) && d.type !== match)
}
const removeTopLevelDoc = (p: any, match: string) => {
    p.documents = (p.documents ?? []).filter(
        (d: any) => !String(d.filename ?? '').includes(match) && d.type !== match
    )
}

const rejected = (reasonCode: string, detailMatch?: string): ExpectedResult => ({
    outcome: 'rejected',
    status: 422,
    reasonCode,
    detailMatch,
})
const accepted: ExpectedResult = { outcome: 'accepted', status: 201, reasonCode: null }

const mutate = (fn: (p: any) => void) => (base: any) => {
    const p = clone(base)
    fn(p)
    return p
}

export const TEST_SUITE: ApiTestCase[] = [
    // ------------------------------------------------------------------
    // Baseline
    // ------------------------------------------------------------------
    {
        id: 'baseline-perfect-client',
        category: 'Baseline',
        name: 'Perfect client — accepted',
        description:
            'The control case: every field well-formed, approved lender, all eligibility checks pass, quantum ≈ £2,100 (> £1,700), per-agreement CFA/LOA/AOF, client-level credit report & AML artefacts, a clean AML result and a valid signature. The API must accept this.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: (base) => clone(base),
    },
    {
        id: 'baseline-two-agreements',
        category: 'Baseline',
        name: 'Two eligible agreements — accepted',
        description: 'Same perfect client with a second fully-eligible agreement. Both become claims (first = base claim).',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => {
            addAgreementWithReportClaim(p, { account_id: 'TEST-AGR-0002', original_amount: 9000 })
        }),
    },

    // ------------------------------------------------------------------
    // Schema — consumer
    // ------------------------------------------------------------------
    {
        id: 'consumer-missing',
        category: 'Schema — Consumer',
        name: 'Consumer block missing',
        description: 'The whole consumer object is absent.',
        failingItem: 'consumer',
        expected: rejected('invalid_data', '"consumer" is required'),
        build: mutate((p) => delete p.consumer),
    },
    {
        id: 'consumer-client-id-missing',
        category: 'Schema — Consumer',
        name: 'client_id missing',
        description: 'No client identifier supplied.',
        failingItem: 'consumer.client_id',
        expected: rejected('invalid_data', 'client_id'),
        build: mutate((p) => delete p.consumer.client_id),
    },
    {
        id: 'consumer-title-missing',
        category: 'Schema — Consumer',
        name: 'Title missing',
        description: 'Title is a hard requirement for funding-batch acceptance.',
        failingItem: 'consumer.title',
        expected: rejected('invalid_data', 'title'),
        build: mutate((p) => (p.consumer.title = null)),
    },
    {
        id: 'consumer-title-digits',
        category: 'Schema — Consumer',
        name: 'Title contains digits',
        description: '"Mr2" is not a valid title — letters only.',
        failingItem: 'consumer.title',
        expected: rejected('invalid_data', 'title'),
        build: mutate((p) => (p.consumer.title = 'Mr2')),
    },
    {
        id: 'consumer-title-too-short',
        category: 'Schema — Consumer',
        name: 'Title too short',
        description: 'Single-character title "M".',
        failingItem: 'consumer.title',
        expected: rejected('invalid_data', 'title'),
        build: mutate((p) => (p.consumer.title = 'M')),
    },
    {
        id: 'consumer-first-name-missing',
        category: 'Schema — Consumer',
        name: 'First name missing',
        failingItem: 'consumer.first_name',
        description: 'First name is required.',
        expected: rejected('invalid_data', 'first_name'),
        build: mutate((p) => (p.consumer.first_name = null)),
    },
    {
        id: 'consumer-first-name-initial',
        category: 'Schema — Consumer',
        name: 'First name is an initial',
        description: '"A" — full names only, initials rejected.',
        failingItem: 'consumer.first_name',
        expected: rejected('invalid_data', 'full name'),
        build: mutate((p) => (p.consumer.first_name = 'A')),
    },
    {
        id: 'consumer-last-name-initial-dot',
        category: 'Schema — Consumer',
        name: 'Last name is an initial with a dot',
        description: '"H." — dots are illegal characters and initials are rejected.',
        failingItem: 'consumer.last_name',
        expected: rejected('invalid_data', 'last_name'),
        build: mutate((p) => (p.consumer.last_name = 'H.')),
    },
    {
        id: 'consumer-name-digits',
        category: 'Schema — Consumer',
        name: 'Name contains digits',
        description: '"Am3lia" — letters, spaces, hyphens and apostrophes only.',
        failingItem: 'consumer.first_name',
        expected: rejected('invalid_data', 'illegal characters'),
        build: mutate((p) => (p.consumer.first_name = 'Am3lia')),
    },
    {
        id: 'consumer-name-profanity',
        category: 'Schema — Consumer',
        name: 'Name contains profanity',
        description: 'Blocked-word check on names.',
        failingItem: 'consumer.last_name',
        expected: rejected('invalid_data', 'blocked word'),
        build: mutate((p) => (p.consumer.last_name = 'Wanker')),
    },
    {
        id: 'consumer-name-placeholder',
        category: 'Schema — Consumer',
        name: 'Name is placeholder junk',
        description: '"Test" as a first name is rejected as a placeholder value.',
        failingItem: 'consumer.first_name',
        expected: rejected('invalid_data', 'blocked word'),
        build: mutate((p) => (p.consumer.first_name = 'Test')),
    },
    {
        id: 'consumer-email-missing',
        category: 'Schema — Consumer',
        name: 'Email missing',
        failingItem: 'consumer.email',
        description: 'Email is required.',
        expected: rejected('invalid_data', 'email'),
        build: mutate((p) => (p.consumer.email = null)),
    },
    {
        id: 'consumer-email-malformed',
        category: 'Schema — Consumer',
        name: 'Email malformed',
        description: '"not-an-email" fails the well-formed email check.',
        failingItem: 'consumer.email',
        expected: rejected('invalid_data', 'email'),
        build: mutate((p) => (p.consumer.email = 'not-an-email')),
    },
    {
        id: 'consumer-phone-missing',
        category: 'Schema — Consumer',
        name: 'Phone missing',
        failingItem: 'consumer.phone',
        description: 'Phone is required.',
        expected: rejected('invalid_data', 'phone'),
        build: mutate((p) => (p.consumer.phone = null)),
    },
    {
        id: 'consumer-phone-too-few-digits',
        category: 'Schema — Consumer',
        name: 'Phone has too few digits',
        description: '"0791 123" — fewer than the 10-digit minimum.',
        failingItem: 'consumer.phone',
        expected: rejected('invalid_data', '10 to 15 digits'),
        build: mutate((p) => (p.consumer.phone = '0791 123')),
    },
    {
        id: 'consumer-phone-illegal-chars',
        category: 'Schema — Consumer',
        name: 'Phone contains letters',
        description: '"07911abc456" — only digits, spaces, +, parentheses and hyphens allowed.',
        failingItem: 'consumer.phone',
        expected: rejected('invalid_data', 'phone'),
        build: mutate((p) => (p.consumer.phone = '07911abc456')),
    },
    {
        id: 'consumer-dob-missing',
        category: 'Schema — Consumer',
        name: 'Date of birth missing',
        failingItem: 'consumer.date_of_birth',
        description: 'DOB is required.',
        expected: rejected('invalid_data', 'date_of_birth'),
        build: mutate((p) => (p.consumer.date_of_birth = null)),
    },
    {
        id: 'consumer-dob-future',
        category: 'Schema — Consumer',
        name: 'Date of birth in the future',
        description: 'A DOB after today is impossible.',
        failingItem: 'consumer.date_of_birth',
        expected: rejected('invalid_data', 'date_of_birth'),
        build: mutate((p) => (p.consumer.date_of_birth = isoDaysAhead(365))),
    },
    {
        id: 'consumer-dob-pre-1900',
        category: 'Schema — Consumer',
        name: 'Date of birth before 1900',
        description: '"1899-12-31" is below the 1900-01-01 floor.',
        failingItem: 'consumer.date_of_birth',
        expected: rejected('invalid_data', 'date_of_birth'),
        build: mutate((p) => (p.consumer.date_of_birth = '1899-12-31')),
    },
    {
        id: 'consumer-dob-impossible',
        category: 'Schema — Consumer',
        name: 'Date of birth is not a real date',
        description: '"1986-02-30" — February 30th does not exist; calendar-rollover dates are caught.',
        failingItem: 'consumer.date_of_birth',
        expected: rejected('invalid_data', 'date_of_birth'),
        build: mutate((p) => (p.consumer.date_of_birth = '1986-02-30')),
    },
    {
        id: 'consumer-dob-wrong-format',
        category: 'Schema — Consumer',
        name: 'Date of birth in UK format',
        description: '"22/03/1986" — only YYYY-MM-DD is accepted.',
        failingItem: 'consumer.date_of_birth',
        expected: rejected('invalid_data', 'date_of_birth'),
        build: mutate((p) => (p.consumer.date_of_birth = '22/03/1986')),
    },

    // ------------------------------------------------------------------
    // Schema — envelope, submission & address
    // ------------------------------------------------------------------
    {
        id: 'submission-reference-missing',
        category: 'Schema — Envelope & Address',
        name: 'Submission reference missing',
        failingItem: 'submission.reference',
        description: 'The submission reference links follow-up deliveries and is required.',
        expected: rejected('invalid_data', 'reference'),
        build: mutate((p) => delete p.submission.reference),
    },
    {
        id: 'enquiries-missing',
        category: 'Schema — Envelope & Address',
        name: 'Enquiries missing entirely',
        failingItem: 'enquiries',
        description: 'No enquiries array and no flattened enquiry block.',
        expected: rejected('invalid_data', 'enquiries'),
        build: mutate((p) => delete p.enquiries),
    },
    {
        id: 'enquiries-empty',
        category: 'Schema — Envelope & Address',
        name: 'Enquiries empty array',
        failingItem: 'enquiries',
        description: 'At least one enquiry is required.',
        expected: rejected('invalid_data', 'enquiries'),
        build: mutate((p) => (p.enquiries = [])),
    },
    {
        id: 'no-current-address',
        category: 'Schema — Envelope & Address',
        name: 'No current address anywhere',
        description: 'Every enquiry lacks address.current — the client is unusable without one.',
        failingItem: 'enquiries[].address.current',
        expected: rejected('invalid_data', 'current address'),
        build: mutate((p) => (p.enquiries[0].address = null)),
    },
    {
        id: 'address-line1-missing',
        category: 'Schema — Envelope & Address',
        name: 'Address line 1 missing',
        failingItem: 'address.current.line_1',
        description: 'line_1 is required on a current address.',
        expected: rejected('invalid_data', 'line_1'),
        build: mutate((p) => delete address(p).line_1),
    },
    {
        id: 'address-city-missing',
        category: 'Schema — Envelope & Address',
        name: 'Address city missing',
        failingItem: 'address.current.city',
        description: 'city is required on a current address.',
        expected: rejected('invalid_data', 'city'),
        build: mutate((p) => delete address(p).city),
    },
    {
        id: 'address-postcode-invalid',
        category: 'Schema — Envelope & Address',
        name: 'Postcode not UK format',
        description: '"12345" is not a valid UK postcode.',
        failingItem: 'address.current.postcode',
        expected: rejected('invalid_data', 'postcode'),
        build: mutate((p) => (address(p).postcode = '12345')),
    },
    {
        id: 'no-agreements',
        category: 'Schema — Envelope & Address',
        name: 'No agreements anywhere',
        description: 'The perfect-client policy: a client with no agreements has no claim to pursue.',
        failingItem: 'enquiries[].agreements',
        expected: rejected('invalid_data', 'at least one agreement'),
        build: mutate((p) => (p.enquiries[0].agreements = [])),
    },

    // ------------------------------------------------------------------
    // Schema — agreement fields
    // ------------------------------------------------------------------
    {
        id: 'agreement-lender-missing',
        category: 'Schema — Agreements',
        name: 'Lender name missing',
        failingItem: 'agreements[0].lender_name',
        description: 'lender_name is required on every agreement.',
        expected: rejected('invalid_data', 'lender_name'),
        build: mutate((p) => delete agr(p).lender_name),
    },
    {
        id: 'agreement-account-id-missing',
        category: 'Schema — Agreements',
        name: 'Account id missing',
        failingItem: 'agreements[0].account_id',
        description: 'account_id (the policy number) is required.',
        expected: rejected('invalid_data', 'account_id'),
        build: mutate((p) => (agr(p).account_id = null)),
    },
    {
        id: 'agreement-account-id-blank',
        category: 'Schema — Agreements',
        name: 'Account id is whitespace only',
        description: '"   " trims to empty and is rejected.',
        failingItem: 'agreements[0].account_id',
        expected: rejected('invalid_data', 'account_id'),
        build: mutate((p) => (agr(p).account_id = '   ')),
    },
    {
        id: 'agreement-status-missing',
        category: 'Schema — Agreements',
        name: 'Status missing',
        failingItem: 'agreements[0].status',
        description: 'status is required and non-empty.',
        expected: rejected('invalid_data', 'status'),
        build: mutate((p) => (agr(p).status = null)),
    },
    {
        id: 'agreement-start-date-missing',
        category: 'Schema — Agreements',
        name: 'Start date missing',
        failingItem: 'agreements[0].start_date',
        description: 'start_date is required.',
        expected: rejected('invalid_data', 'start_date'),
        build: mutate((p) => (agr(p).start_date = null)),
    },
    {
        id: 'agreement-start-date-bad-format',
        category: 'Schema — Agreements',
        name: 'Start date in UK format',
        description: '"01/06/2018" — only YYYY-MM-DD is accepted.',
        failingItem: 'agreements[0].start_date',
        expected: rejected('invalid_data', 'start_date'),
        build: mutate((p) => (agr(p).start_date = '01/06/2018')),
    },
    {
        id: 'agreement-start-date-impossible',
        category: 'Schema — Agreements',
        name: 'Start date is not a real date',
        description: '"2018-02-30" — impossible calendar date.',
        failingItem: 'agreements[0].start_date',
        expected: rejected('invalid_data', 'start_date'),
        build: mutate((p) => (agr(p).start_date = '2018-02-30')),
    },
    {
        id: 'agreement-amount-missing',
        category: 'Schema — Agreements',
        name: 'Original amount missing',
        failingItem: 'agreements[0].original_amount',
        description: 'original_amount is required.',
        expected: rejected('invalid_data', 'original_amount'),
        build: mutate((p) => (agr(p).original_amount = null)),
    },
    {
        id: 'agreement-amount-zero',
        category: 'Schema — Agreements',
        name: 'Original amount is zero',
        description: 'Must be greater than 0.',
        failingItem: 'agreements[0].original_amount',
        expected: rejected('invalid_data', 'original_amount'),
        build: mutate((p) => (agr(p).original_amount = 0)),
    },
    {
        id: 'agreement-amount-negative',
        category: 'Schema — Agreements',
        name: 'Original amount is negative',
        description: '-12000 is rejected.',
        failingItem: 'agreements[0].original_amount',
        expected: rejected('invalid_data', 'original_amount'),
        build: mutate((p) => (agr(p).original_amount = -12000)),
    },
    {
        id: 'agreement-monthly-missing',
        category: 'Schema — Agreements',
        name: 'Monthly payment missing',
        failingItem: 'agreements[0].monthly_payment_amount',
        description: 'monthly_payment_amount is required.',
        expected: rejected('invalid_data', 'monthly_payment_amount'),
        build: mutate((p) => (agr(p).monthly_payment_amount = null)),
    },
    {
        id: 'agreement-monthly-zero',
        category: 'Schema — Agreements',
        name: 'Monthly payment is zero',
        description: 'Must be greater than 0.',
        failingItem: 'agreements[0].monthly_payment_amount',
        expected: rejected('invalid_data', 'monthly_payment_amount'),
        build: mutate((p) => (agr(p).monthly_payment_amount = 0)),
    },
    {
        id: 'agreement-term-missing',
        category: 'Schema — Agreements',
        name: 'Loan term missing',
        failingItem: 'agreements[0].loan_term',
        description: 'loan_term is required.',
        expected: rejected('invalid_data', 'loan_term'),
        build: mutate((p) => (agr(p).loan_term = null)),
    },
    {
        id: 'agreement-term-four-months',
        category: 'Schema — Agreements',
        name: 'Loan term of 4 months',
        description: 'Contracts of 4 months or less are a hard fail (minimum 5). A Jan–Mar style 3-month term is also rejected.',
        failingItem: 'agreements[0].loan_term',
        expected: rejected('invalid_data', 'at least 5 months'),
        build: mutate((p) => (agr(p).loan_term = 4)),
    },
    {
        id: 'agreement-term-three-months',
        category: 'Schema — Agreements',
        name: 'Loan term of 3 months',
        description: 'A 3-month contract (e.g. Jan 2020–Mar 2020) fully rejects the delivery.',
        failingItem: 'agreements[0].loan_term',
        expected: rejected('invalid_data', 'at least 5 months'),
        build: mutate((p) => (agr(p).loan_term = 3)),
    },
    {
        id: 'agreement-term-non-integer',
        category: 'Schema — Agreements',
        name: 'Loan term not a whole number',
        description: '12.5 months — must be an integer.',
        failingItem: 'agreements[0].loan_term',
        expected: rejected('invalid_data', 'loan_term'),
        build: mutate((p) => (agr(p).loan_term = 12.5)),
    },
    {
        id: 'agreement-second-broken',
        category: 'Schema — Agreements',
        name: 'Second agreement invalid, first perfect',
        description: 'One good agreement plus one missing its start_date — the whole delivery is still rejected.',
        failingItem: 'agreements[1].start_date',
        expected: rejected('invalid_data', 'start_date'),
        build: mutate((p) => {
            const second = { ...agr(p), account_id: 'TEST-AGR-0002' }
            delete second.start_date
            p.enquiries[0].agreements.push(second)
        }),
    },

    // ------------------------------------------------------------------
    // Schema — signature audit
    // ------------------------------------------------------------------
    {
        id: 'signature-block-missing',
        category: 'Schema — Signature Audit',
        name: 'Signature block missing',
        failingItem: 'signature',
        description: 'The whole signature object is absent.',
        expected: rejected('invalid_data', 'signature'),
        build: mutate((p) => delete p.signature),
    },
    {
        id: 'audit-missing',
        category: 'Schema — Signature Audit',
        name: 'Audit trail missing',
        failingItem: 'signature.audit',
        description: 'The e-sign audit object is mandatory.',
        expected: rejected('invalid_data', 'audit'),
        build: mutate((p) => delete p.signature.audit),
    },
    {
        id: 'audit-signed-at-missing',
        category: 'Schema — Signature Audit',
        name: 'signed_at missing',
        failingItem: 'signature.audit.signed_at',
        description: 'The opt-in moment is required.',
        expected: rejected('invalid_data', 'signed_at'),
        build: mutate((p) => delete audit(p).signed_at),
    },
    {
        id: 'audit-signed-at-date-only',
        category: 'Schema — Signature Audit',
        name: 'signed_at is date-only',
        description: 'A date without a time is not fully qualified.',
        failingItem: 'signature.audit.signed_at',
        expected: rejected('invalid_data', 'fully qualified'),
        build: mutate((p) => (audit(p).signed_at = isoDaysAgo(1).slice(0, 10))),
    },
    {
        id: 'audit-signed-at-future',
        category: 'Schema — Signature Audit',
        name: 'signed_at in the future',
        description: 'A signature timestamp ahead of now is rejected.',
        failingItem: 'signature.audit.signed_at',
        expected: rejected('invalid_data', 'future'),
        build: mutate((p) => (audit(p).signed_at = `${isoDaysAhead(200)} 10:00:00`)),
    },
    {
        id: 'audit-ip-missing',
        category: 'Schema — Signature Audit',
        name: 'IP address missing',
        failingItem: 'signature.audit.ip_address',
        description: 'The signing IP is required.',
        expected: rejected('invalid_data', 'ip_address'),
        build: mutate((p) => delete audit(p).ip_address),
    },
    {
        id: 'audit-ip-invalid',
        category: 'Schema — Signature Audit',
        name: 'IP address invalid',
        description: '"999.12.1" is not a valid IPv4/IPv6 address.',
        failingItem: 'signature.audit.ip_address',
        expected: rejected('invalid_data', 'ip_address'),
        build: mutate((p) => (audit(p).ip_address = '999.12.1')),
    },
    {
        id: 'audit-ip-cidr',
        category: 'Schema — Signature Audit',
        name: 'IP address is a CIDR range',
        description: '"86.140.55.12/24" — ranges are rejected, single addresses only.',
        failingItem: 'signature.audit.ip_address',
        expected: rejected('invalid_data', 'ip_address'),
        build: mutate((p) => (audit(p).ip_address = '86.140.55.12/24')),
    },
    {
        id: 'audit-no-device-or-browser',
        category: 'Schema — Signature Audit',
        name: 'No device and no browser info',
        description: 'Both user_agent and device_type missing — where the e-sign took place is unknown.',
        failingItem: 'signature.audit.user_agent / device_type',
        expected: rejected('invalid_data', 'user_agent or device_type'),
        build: mutate((p) => {
            audit(p).user_agent = null
            audit(p).device_type = ''
        }),
    },

    // ------------------------------------------------------------------
    // Lender approval gate
    // ------------------------------------------------------------------
    {
        id: 'lender-unapproved',
        category: 'Lender Approval',
        name: 'Unapproved lender',
        description: `"${UNAPPROVED_LENDER}" is not on the approved defendant list.`,
        failingItem: 'agreements[0].lender_name (defendant list)',
        expected: rejected('lender_not_approved', UNAPPROVED_LENDER),
        build: mutate((p) => (agr(p).lender_name = UNAPPROVED_LENDER)),
    },
    {
        id: 'lender-mixed',
        category: 'Lender Approval',
        name: 'One approved + one unapproved lender',
        description: 'A perfect agreement plus a second with an unapproved lender — the whole delivery is rejected.',
        failingItem: 'agreements[1].lender_name (defendant list)',
        expected: rejected('lender_not_approved', UNAPPROVED_LENDER),
        build: mutate((p) => {
            p.enquiries[0].agreements.push({
                ...agr(p),
                account_id: 'TEST-AGR-0002',
                lender_name: UNAPPROVED_LENDER,
            })
        }),
    },

    // ------------------------------------------------------------------
    // Eligibility gate
    // ------------------------------------------------------------------
    {
        id: 'eligibility-below-min-loan',
        category: 'Eligibility',
        name: 'Loan below £5,000',
        description: '£3,000 original amount fails the minimum loan value.',
        failingItem: 'agreements[0].original_amount (≥ £5,000)',
        expected: rejected('client_not_eligible', 'below_minimum_loan'),
        build: mutate((p) => (agr(p).original_amount = 3000)),
    },
    {
        id: 'eligibility-pre-2007',
        category: 'Eligibility',
        name: 'Agreement started before 2007',
        description: 'Start date 2005-06-01 is before the 2007-01-01 floor.',
        failingItem: 'agreements[0].start_date (≥ 2007-01-01)',
        expected: rejected('client_not_eligible', 'pre_2007_start_date'),
        build: mutate((p) => (agr(p).start_date = '2005-06-01')),
    },
    {
        id: 'eligibility-post-oct-2024',
        category: 'Eligibility',
        name: 'Agreement started on/after 1 Oct 2024',
        description: 'Start date 2025-01-15 is past the hard cutoff.',
        failingItem: 'agreements[0].start_date (< 2024-10-01)',
        expected: rejected('client_not_eligible', 'post_october_2024_start_date'),
        build: mutate((p) => (agr(p).start_date = '2025-01-15')),
    },
    {
        id: 'eligibility-future-start',
        category: 'Eligibility',
        name: 'Agreement starts in the future',
        description: 'A start date after today passes the schema (real date) but fails eligibility.',
        failingItem: 'agreements[0].start_date (not future)',
        expected: rejected('client_not_eligible', 'future_start_date'),
        build: mutate((p) => (agr(p).start_date = isoDaysAhead(400))),
    },
    {
        id: 'eligibility-under-18',
        category: 'Eligibility',
        name: 'Claimant under 18 at agreement start',
        description: 'DOB 2003-01-10 with a 2018-06-01 start — the client was 15 when the agreement began.',
        failingItem: 'consumer.date_of_birth vs agreements[0].start_date',
        expected: rejected('client_not_eligible', 'claimant_under_18'),
        build: mutate((p) => (p.consumer.date_of_birth = '2003-01-10')),
    },
    {
        id: 'eligibility-repayment-low',
        category: 'Eligibility',
        name: 'Monthly repayment below £50',
        description: '£25/month fails the repayment floor.',
        failingItem: 'agreements[0].monthly_payment_amount (≥ £50)',
        expected: rejected('client_not_eligible', 'below_minimum_monthly_repayment'),
        build: mutate((p) => (agr(p).monthly_payment_amount = 25)),
    },
    {
        id: 'eligibility-repayment-high',
        category: 'Eligibility',
        name: 'Monthly repayment above £1,500',
        description: '£2,000/month fails the repayment ceiling.',
        failingItem: 'agreements[0].monthly_payment_amount (≤ £1,500)',
        expected: rejected('client_not_eligible', 'excessive_monthly_repayment'),
        build: mutate((p) => (agr(p).monthly_payment_amount = 2000)),
    },
    {
        id: 'eligibility-one-bad-agreement',
        category: 'Eligibility',
        name: 'One eligible + one ineligible agreement',
        description: 'The second agreement is only £3,500 — one ineligible agreement rejects the whole client.',
        failingItem: 'agreements[1].original_amount (≥ £5,000)',
        expected: rejected('client_not_eligible', 'below_minimum_loan'),
        build: mutate((p) => {
            p.enquiries[0].agreements.push({ ...agr(p), account_id: 'TEST-AGR-0002', original_amount: 3500 })
        }),
    },

    // ------------------------------------------------------------------
    // Aggregate value (quantum)
    // ------------------------------------------------------------------
    {
        id: 'quantum-below-threshold',
        category: 'Aggregate Value',
        name: 'Client quantum below £1,700',
        description:
            'A single £5,000 loan from 2023 passes every per-agreement check but values at only ≈ £670 (10% commission + 8%/yr statutory interest to signed_at + 14 months).',
        failingItem: 'client aggregate quantum (≥ £1,700)',
        expected: rejected('client_not_eligible', 'below_minimum_client_value'),
        build: mutate((p) => {
            agr(p).original_amount = 5000
            agr(p).start_date = '2023-06-01'
        }),
    },
    {
        id: 'quantum-two-still-below',
        category: 'Aggregate Value',
        name: 'Two low-value agreements still below £1,700',
        description: 'Two £5,000 loans from 2023 sum to ≈ £1,340 — still short of the threshold.',
        failingItem: 'client aggregate quantum (≥ £1,700)',
        expected: rejected('client_not_eligible', 'below_minimum_client_value'),
        build: mutate((p) => {
            agr(p).original_amount = 5000
            agr(p).start_date = '2023-06-01'
            p.enquiries[0].agreements.push({ ...agr(p), account_id: 'TEST-AGR-0002' })
        }),
    },
    {
        id: 'quantum-three-pass',
        category: 'Aggregate Value',
        name: 'Three low-value agreements clear £1,700 — accepted',
        description: 'Three £5,000 loans from 2023 sum to ≈ £2,010 and pass the threshold.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => {
            agr(p).original_amount = 5000
            agr(p).start_date = '2023-06-01'
            addAgreementWithReportClaim(p, { account_id: 'TEST-AGR-0002' })
            addAgreementWithReportClaim(p, { account_id: 'TEST-AGR-0003' })
        }),
    },

    // ------------------------------------------------------------------
    // Required documents gate
    // ------------------------------------------------------------------
    {
        id: 'docs-missing-cfa',
        category: 'Documents',
        name: 'CFA missing',
        failingItem: 'agreements[0].documents (cfa)',
        description: 'The signed Conditional Fee Agreement is absent from the agreement document set.',
        expected: rejected('missing_documents', 'cfa'),
        build: mutate((p) => removeDoc(p, 'conditional-fee')),
    },
    {
        id: 'docs-missing-loa',
        category: 'Documents',
        name: 'LOA missing',
        failingItem: 'agreements[0].documents (loa)',
        description: 'The signed Letter of Authority is absent from the agreement document set.',
        expected: rejected('missing_documents', 'loa'),
        build: mutate((p) => removeDoc(p, 'letter-of-authority')),
    },
    {
        id: 'docs-missing-aof',
        category: 'Documents',
        name: 'AOF missing',
        failingItem: 'agreements[0].documents (aof)',
        description: 'The signed Authority to Act / Assignment of Funds is absent from the agreement document set.',
        expected: rejected('missing_documents', 'aof'),
        build: mutate((p) => removeDoc(p, 'authority-to-act')),
    },
    {
        id: 'docs-credit-report-doc-optional',
        category: 'Documents',
        name: 'Credit report document absent — accepted (v2)',
        failingItem: 'nothing — must be accepted',
        description:
            'Top-level credit_search in documents[] is optional — removing it must NOT reject the delivery.',
        expected: accepted,
        build: mutate((p) => removeTopLevelDoc(p, 'credit_search')),
    },
    {
        id: 'docs-missing-all',
        category: 'Documents',
        name: 'No claim documents at all',
        failingItem: 'agreements[0].documents (all three)',
        description: 'Empty agreement documents array — all three required documents (CFA, LOA, AOF) reported missing.',
        expected: rejected('missing_documents', 'aof'),
        build: mutate((p) => {
            agr(p).documents = []
        }),
    },
    {
        id: 'docs-top-level-only-hard-cut',
        category: 'Documents',
        name: 'Top-level claim documents only — rejected (hard cut)',
        failingItem: 'agreements[0].documents',
        description:
            'CFA/LOA/AOF on top-level documents[] no longer satisfy the gate — each agreement must carry its own set. Top-level claim docs also fail schema (only aml/credit_search allowed there).',
        expected: rejected('invalid_data', 'documents'),
        build: mutate((p) => {
            agr(p).documents = []
            p.documents = [
                {
                    type: 'tcs',
                    filename: 'conditional-fee-agreement-signed.pdf',
                    url: 'https://files.example.com/dl/cfa',
                    mime_type: 'application/pdf',
                },
                {
                    type: 'tcs',
                    filename: 'letter-of-authority-signed.pdf',
                    url: 'https://files.example.com/dl/loa',
                    mime_type: 'application/pdf',
                },
                {
                    type: 'tcs',
                    filename: 'authority-to-act-signed.pdf',
                    url: 'https://files.example.com/dl/aof',
                    mime_type: 'application/pdf',
                },
            ]
        }),
    },
    {
        id: 'docs-enquiry-documents-forbidden',
        category: 'Documents',
        name: 'Enquiry-level documents rejected',
        failingItem: 'enquiries[0].documents',
        description: 'Enquiry-level documents[] is no longer accepted.',
        expected: rejected('invalid_data', 'documents'),
        build: mutate((p) => {
            p.enquiries[0].documents = claimDocs()
        }),
    },
    {
        id: 'docs-second-agreement-missing-set',
        category: 'Documents',
        name: 'Second agreement missing document set',
        failingItem: 'agreements[1].documents',
        description: 'A multi-claim delivery fails when any agreement lacks CFA/LOA/AOF.',
        expected: rejected('missing_documents', 'cfa'),
        build: mutate((p) => {
            p.enquiries[0].agreements.push({
                ...agr(p),
                account_id: 'TEST-AGR-0002',
                original_amount: 9000,
                documents: [],
            })
        }),
    },

    // ------------------------------------------------------------------
    // Signature image gate
    // ------------------------------------------------------------------
    {
        id: 'signature-image-missing',
        category: 'Signature Image',
        name: 'Signature image missing',
        description: 'image_base64 is null (audit block intact, so schema passes and the signature gate catches it).',
        failingItem: 'signature.image_base64',
        expected: rejected('invalid_signature', 'missing'),
        build: mutate((p) => (p.signature.image_base64 = null)),
    },
    {
        id: 'signature-image-not-base64',
        category: 'Signature Image',
        name: 'Signature is not valid base64',
        description: '"%%%not-base64%%%" fails strict base64 decoding.',
        failingItem: 'signature.image_base64',
        expected: rejected('invalid_signature', 'not valid base64'),
        build: mutate((p) => (p.signature.image_base64 = '%%%not-base64%%%')),
    },
    {
        id: 'signature-image-not-png',
        category: 'Signature Image',
        name: 'Valid base64 but not a PNG',
        description: 'base64 of "hello world" decodes fine but has no PNG signature bytes.',
        failingItem: 'signature.image_base64',
        expected: rejected('invalid_signature', 'not valid base64'),
        build: mutate((p) => (p.signature.image_base64 = 'aGVsbG8gd29ybGQ=')),
    },

    // ------------------------------------------------------------------
    // Client artefacts & AML gate (v2) — credit_report_pdf / aml_pdf /
    // credit_report_json and the real-time identity/AML checks
    // ------------------------------------------------------------------
    {
        id: 'artefacts-all-omitted',
        category: 'Client Artefacts & AML (v2)',
        name: 'All three artefacts omitted — accepted',
        description:
            'credit_report_pdf, aml_pdf and credit_report_json are all optional. With none of them present the AML gate is skipped and the delivery is accepted.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => {
            delete p.credit_report_pdf
            delete p.aml_pdf
            delete p.credit_report_json
        }),
    },
    {
        id: 'artefacts-credit-report-pdf-bad-url',
        category: 'Client Artefacts & AML (v2)',
        name: 'credit_report_pdf URL invalid',
        description: 'When credit_report_pdf is present its url must be a valid URI.',
        failingItem: 'credit_report_pdf.url',
        expected: rejected('invalid_data', 'credit_report_pdf'),
        build: mutate((p) => (p.credit_report_pdf.url = 'not-a-url')),
    },
    {
        id: 'artefacts-aml-pdf-bad-url',
        category: 'Client Artefacts & AML (v2)',
        name: 'aml_pdf URL invalid',
        description: 'When aml_pdf is present its url must be a valid URI.',
        failingItem: 'aml_pdf.url',
        expected: rejected('invalid_data', 'aml_pdf'),
        build: mutate((p) => (p.aml_pdf.url = 'not-a-url')),
    },
    {
        id: 'artefacts-credit-report-json-garbage',
        category: 'Client Artefacts & AML (v2)',
        name: 'credit_report_json is not JSON',
        description: 'A string that does not parse to a JSON object is rejected at schema validation.',
        failingItem: 'credit_report_json',
        expected: rejected('invalid_data', 'credit_report_json'),
        build: mutate((p) => (p.credit_report_json = '%%% not json %%%')),
    },
    {
        id: 'credit-report-prior-industry-search',
        category: 'Credit Report Consistency',
        name: 'Previous industry search found',
        description:
            'A non-empty previous_industry_matches list means the client has a prior industry search and the full delivery is rejected.',
        failingItem: 'credit_report_json.previous_industry_matches',
        expected: rejected('credit_report_check_failed', 'previous_industry_search_found'),
        build: mutate((p) => {
            p.credit_report_json.has_previous_industry_search = true
            p.credit_report_json.previous_industry_matches = [
                {
                    company_name: 'CHECKBOARD LIMITED',
                    industry: 'claims_management',
                    search_date: '2026-02-14',
                    search_type: 'creditfileRequest',
                },
            ]
        }),
    },
    {
        id: 'credit-report-industry-search-empty',
        category: 'Credit Report Consistency',
        name: 'Industry search data empty — accepted',
        description: 'Missing/empty industry-search fields are clean and do not reject the delivery.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => {
            delete p.credit_report_json.has_previous_industry_search
            delete p.credit_report_json.previous_industry_matches
        }),
    },
    {
        id: 'credit-report-agreement-missing',
        category: 'Credit Report Consistency',
        name: 'Payload agreement absent from report',
        description:
            'The payload account_id does not exist in credit_report_json.claims, so the full delivery is rejected.',
        failingItem: 'agreements[0].account_id',
        expected: rejected('credit_report_check_failed', 'credit_report_agreement_missing'),
        build: mutate((p) => (agr(p).account_id = 'NOT-IN-CREDIT-REPORT')),
    },
    {
        id: 'credit-report-lender-mismatch',
        category: 'Credit Report Consistency',
        name: 'Agreement lender differs from report',
        description:
            'The agreement number exists in credit_report_json.claims but its lender differs from the payload lender.',
        failingItem: 'credit_report_json.claims[0].lender_name',
        expected: rejected('credit_report_check_failed', 'credit_report_lender_mismatch'),
        build: mutate((p) => (p.credit_report_json.claims[0].lender_name = 'Different Finance Limited')),
    },
    {
        id: 'credit-report-original-amount-mismatch',
        category: 'Credit Report Consistency',
        name: 'Original loan amount differs',
        description:
            'The matched report claim has a different original_amount from the payload agreement.',
        failingItem: 'credit_report_json.claims[0].original_amount',
        expected: rejected('credit_report_check_failed', 'credit_report_original_amount_mismatch'),
        build: mutate((p) => (p.credit_report_json.claims[0].original_amount = 11999.99)),
    },
    {
        id: 'credit-report-balance-mismatch',
        category: 'Credit Report Consistency',
        name: 'Agreement balance differs',
        description: 'The matched report claim has a different balance from the payload agreement.',
        failingItem: 'credit_report_json.claims[0].balance',
        expected: rejected('credit_report_check_failed', 'credit_report_balance_mismatch'),
        build: mutate((p) => (p.credit_report_json.claims[0].balance = 1)),
    },
    {
        id: 'credit-report-status-defaulted',
        category: 'Credit Report Consistency',
        name: 'Report agreement is defaulted',
        description:
            'Default, failed and delinquent statuses are rejected; defaulted rejects the full delivery.',
        failingItem: 'credit_report_json.claims[0].status',
        expected: rejected('credit_report_check_failed', 'credit_report_agreement_status_not_accepted'),
        build: mutate((p) => (p.credit_report_json.claims[0].status = 'defaulted')),
    },
    {
        id: 'credit-report-status-late',
        category: 'Credit Report Consistency',
        name: 'Report agreement is late — accepted',
        description: 'Late agreements are accepted; late payment remains a rejecting status.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => (p.credit_report_json.claims[0].status = 'late')),
    },
    {
        id: 'credit-report-status-settled',
        category: 'Credit Report Consistency',
        name: 'Report agreement is settled — accepted',
        description: 'Settled is one of the explicitly accepted report agreement statuses.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => (p.credit_report_json.claims[0].status = 'settled')),
    },
    {
        id: 'credit-report-status-active',
        category: 'Credit Report Consistency',
        name: 'Report agreement is active — accepted',
        description: 'Active and in-progress agreements are explicitly accepted.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => (p.credit_report_json.claims[0].status = 'active')),
    },
    {
        id: 'aml-identity-failed',
        category: 'Client Artefacts & AML (v2)',
        name: 'Identity check failed',
        description: 'aml_data.identityResult is "Fail" — the client cannot be accepted.',
        failingItem: 'credit_report_json.aml_data.identityResult',
        expected: rejected('aml_check_failed', 'aml_identity_failed'),
        build: mutate((p) => (p.credit_report_json.aml_data.identityResult = 'Fail')),
    },
    {
        id: 'aml-identity-referred',
        category: 'Client Artefacts & AML (v2)',
        name: 'Identity check referred',
        description: 'A "Refer" identity decision is treated as not passed.',
        failingItem: 'credit_report_json.aml_data.identityResult',
        expected: rejected('aml_check_failed', 'aml_identity_failed'),
        build: mutate((p) => (p.credit_report_json.aml_data.identityResult = 'Refer')),
    },
    {
        id: 'aml-name-mismatch-tolerated',
        category: 'Client Artefacts & AML (v2)',
        name: 'Bureau-rendered matched name differs — accepted',
        description:
            'aml_data.nameMatched ("MR JOHN SMITH") differs from the consumer (Amelia Hartley). This bureau-rendered field remains informational; credit_report_json.search_name is the name gated against the consumer.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => (p.credit_report_json.aml_data.nameMatched = 'MR JOHN SMITH')),
    },
    {
        id: 'aml-search-surname-mismatch',
        category: 'Client Artefacts & AML (v2)',
        name: 'Credit-search surname differs',
        description:
            'credit_report_json.search_name is "Alan Tupling" but consumer.last_name is "Hartley" — the report may belong to another person, so the full delivery is rejected.',
        failingItem: 'credit_report_json.search_name',
        expected: rejected('aml_check_failed', 'aml_last_name_mismatch'),
        build: mutate((p) => (p.credit_report_json.search_name = 'Alan Tupling')),
    },
    {
        id: 'aml-search-name-missing',
        category: 'Client Artefacts & AML (v2)',
        name: 'Credit-search name missing',
        description: 'A report without search_name cannot be tied to the consumer and fails closed.',
        failingItem: 'credit_report_json.search_name',
        expected: rejected('aml_check_failed', 'aml_search_name_missing'),
        build: mutate((p) => delete p.credit_report_json.search_name),
    },
    {
        id: 'aml-dob-mismatch',
        category: 'Client Artefacts & AML (v2)',
        name: 'AML matched date of birth differs',
        description:
            'aml_data.dateOfBirthMatched ("1990-11-05") does not equal the consumer\'s date of birth — date of birth is the identity field still gated.',
        failingItem: 'credit_report_json.aml_data.dateOfBirthMatched',
        expected: rejected('aml_check_failed', 'aml_identity_mismatch'),
        build: mutate((p) => (p.credit_report_json.aml_data.dateOfBirthMatched = '1990-11-05')),
    },
    {
        id: 'aml-too-many-ccjs',
        category: 'Client Artefacts & AML (v2)',
        name: 'More than 4 CCJs',
        description: '5 judgment records in aml_data.ccjs — over the 4-CCJ ceiling.',
        failingItem: 'credit_report_json.aml_data.ccjs',
        expected: rejected('aml_check_failed', 'aml_too_many_ccjs'),
        build: mutate((p) => {
            p.credit_report_json.aml_data.ccjs = [1, 2, 3, 4, 5].map(buildTestCcjRecord)
        }),
    },
    {
        id: 'aml-ccjs-at-threshold',
        category: 'Client Artefacts & AML (v2)',
        name: 'Exactly 4 CCJs — accepted',
        description: 'The rule is MORE than 4 CCJs; exactly 4 judgment records is still acceptable.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => {
            p.credit_report_json.aml_data.ccjs = [1, 2, 3, 4].map(buildTestCcjRecord)
        }),
    },
    {
        id: 'aml-has-iva',
        category: 'Client Artefacts & AML (v2)',
        name: 'IVA on record',
        description:
            'An "Individual Voluntary Arrangement Made" order in aml_data.insolvencies rejects the client.',
        failingItem: 'credit_report_json.aml_data.insolvencies (IVA order)',
        expected: rejected('aml_check_failed', 'aml_has_iva'),
        build: mutate((p) => {
            p.credit_report_json.aml_data.insolvencies = [TEST_IVA_RECORD]
        }),
    },
    {
        id: 'aml-has-bankruptcy',
        category: 'Client Artefacts & AML (v2)',
        name: 'Bankruptcy on record',
        description: 'A "Bankruptcy Order" in aml_data.insolvencies rejects the client.',
        failingItem: 'credit_report_json.aml_data.insolvencies (bankruptcy order)',
        expected: rejected('aml_check_failed', 'aml_has_bankruptcy'),
        build: mutate((p) => {
            p.credit_report_json.aml_data.insolvencies = [TEST_BANKRUPTCY_RECORD]
        }),
    },
    {
        id: 'aml-multiple-failures-all-listed',
        category: 'Client Artefacts & AML (v2)',
        name: 'IVA + bankruptcy + 6 CCJs + failed identity',
        description: 'Every failed AML check is listed in detail.checks — not just the first.',
        failingItem: 'identityResult, ccjs, insolvencies',
        expected: rejected('aml_check_failed', 'aml_has_iva'),
        build: mutate((p) => {
            p.credit_report_json.aml_data.identityResult = 'Fail'
            p.credit_report_json.aml_data.ccjs = [1, 2, 3, 4, 5, 6].map(buildTestCcjRecord)
            p.credit_report_json.aml_data.insolvencies = [TEST_IVA_RECORD, TEST_BANKRUPTCY_RECORD]
        }),
    },
    {
        id: 'aml-json-as-string-accepted',
        category: 'Client Artefacts & AML (v2)',
        name: 'credit_report_json sent as a JSON string — accepted',
        description: 'The contract allows a string that parses to an object; the AML gate still runs against it.',
        failingItem: 'nothing — must be accepted',
        expected: accepted,
        build: mutate((p) => (p.credit_report_json = JSON.stringify(p.credit_report_json))),
    },
    {
        id: 'aml-json-string-with-iva-rejected',
        category: 'Client Artefacts & AML (v2)',
        name: 'Stringified JSON with an IVA — still caught',
        description: 'AML data inside a stringified credit_report_json is parsed and gated identically.',
        failingItem: 'credit_report_json (string) aml_data.insolvencies',
        expected: rejected('aml_check_failed', 'aml_has_iva'),
        build: mutate((p) => {
            p.credit_report_json.aml_data.insolvencies = [TEST_IVA_RECORD]
            p.credit_report_json = JSON.stringify(p.credit_report_json)
        }),
    },

    // ------------------------------------------------------------------
    // Combinations — gate ordering & multi-failure payloads
    // ------------------------------------------------------------------
    {
        id: 'combo-schema-beats-lender',
        category: 'Combinations',
        name: 'Bad email + unapproved lender',
        description:
            'Both broken — schema validation runs first, so the rejection is invalid_data (the lender problem would surface on resubmission).',
        failingItem: 'consumer.email, then lender_name',
        expected: rejected('invalid_data', 'email'),
        build: mutate((p) => {
            p.consumer.email = 'broken'
            agr(p).lender_name = UNAPPROVED_LENDER
        }),
    },
    {
        id: 'combo-lender-beats-eligibility',
        category: 'Combinations',
        name: 'Unapproved lender + ineligible loan',
        description: 'Lender approval runs before eligibility, so the rejection is lender_not_approved.',
        failingItem: 'lender_name, then original_amount',
        expected: rejected('lender_not_approved', UNAPPROVED_LENDER),
        build: mutate((p) => {
            agr(p).lender_name = UNAPPROVED_LENDER
            agr(p).original_amount = 3000
        }),
    },
    {
        id: 'combo-eligibility-beats-docs',
        category: 'Combinations',
        name: 'Ineligible loan + missing documents',
        description: 'Eligibility runs before the documents gate, so the rejection is client_not_eligible.',
        failingItem: 'original_amount, then documents',
        expected: rejected('client_not_eligible', 'below_minimum_loan'),
        build: mutate((p) => {
            agr(p).original_amount = 3000
            agr(p).documents = []
        }),
    },
    {
        id: 'combo-docs-beat-signature',
        category: 'Combinations',
        name: 'Missing documents + broken signature',
        description: 'The documents gate runs before the signature gate, so the rejection is missing_documents.',
        failingItem: 'agreements[0].documents, then signature.image_base64',
        expected: rejected('missing_documents', 'cfa'),
        build: mutate((p) => {
            removeDoc(p, 'conditional-fee')
            p.signature.image_base64 = '%%%not-base64%%%'
        }),
    },
    {
        id: 'combo-quantum-beats-docs',
        category: 'Combinations',
        name: 'Under-quantum client + missing CFA',
        description: 'The aggregate value rule (eligibility gate) fires before the documents gate.',
        failingItem: 'client aggregate quantum, then agreements[0].documents',
        expected: rejected('client_not_eligible', 'below_minimum_client_value'),
        build: mutate((p) => {
            agr(p).original_amount = 5000
            agr(p).start_date = '2023-06-01'
            removeDoc(p, 'conditional-fee')
        }),
    },
    {
        id: 'combo-signature-beats-aml',
        category: 'Combinations',
        name: 'Broken signature + failed AML',
        description: 'The signature gate runs before the AML gate, so the rejection is invalid_signature.',
        failingItem: 'signature.image_base64, then credit_report_json AML',
        expected: rejected('invalid_signature', 'missing'),
        build: mutate((p) => {
            p.signature.image_base64 = null
            p.credit_report_json.aml_data.insolvencies = [TEST_IVA_RECORD]
        }),
    },
    {
        id: 'combo-many-schema-errors',
        category: 'Combinations',
        name: 'Kitchen sink of schema failures',
        description:
            'Initial name + bad email + short phone + bad postcode + 4-month term all at once — every error is listed in a single invalid_data response (validation does not stop at the first failure).',
        failingItem: 'first_name, email, phone, postcode, loan_term',
        expected: rejected('invalid_data', 'loan_term'),
        build: mutate((p) => {
            p.consumer.first_name = 'A'
            p.consumer.email = 'nope'
            p.consumer.phone = '123'
            address(p).postcode = 'NOT-A-POSTCODE'
            agr(p).loan_term = 4
        }),
    },
]

export const TEST_CATEGORIES: string[] = Array.from(new Set(TEST_SUITE.map((t) => t.category)))
